import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement, type ReactNode } from "react";
import {
  ConfirmDialog,
  ContentHeader,
  Field,
  Modal,
  QtyInput,
  QueryStatus,
  StatusBadge,
  useToast,
} from "@cx/ui";
import type {
  PurchaseOrderFormOptions,
  PurchaseOrderHistoryItem,
  PurchaseOrderStatusRef,
  PurchaseOrderView,
  PurchaseReceiptView,
} from "@cx/contracts";
import {
  FREIGHT_MODE_LABELS,
  formatBRL,
  formatDateBR,
  formatDocument,
  formatPct,
  formatQty,
  todayInSaoPaulo,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import {
  PROGRESS_LABELS,
  RECEIPT_SOURCE_LABELS,
  purchaseHistoryLabel,
  receiptEffects,
  receiptTitle,
  receiveDefaults,
  receiveItems,
  receiveProblem,
  type ReceiveLotRow,
  type ReceiveRow,
} from "./labels";

const dateTimeBR = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });

/**
 * Pedido de compra: dados, itens com recebido/saldo, totais, linha do tempo dos recebimentos (estornáveis), situação
 * com histórico; "Receber" (parcial ou total) e "Cancelar saldo".
 */
export function PurchaseOrderViewPage(): ReactElement {
  const { id } = useParams({ from: "/suprimentos/pedidos-de-compra/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const order = useQuery({
    queryKey: ["purchase-orders", companyId, "view", id],
    refetchOnMount: "always",
    queryFn: () => api<PurchaseOrderView>(`/purchase-orders/${id}`),
  });
  const history = useQuery({
    queryKey: ["purchase-orders", companyId, "history", id],
    refetchOnMount: "always",
    queryFn: () => api<{ data: PurchaseOrderHistoryItem[] }>(`/purchase-orders/${id}/status-history`),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["purchase-orders", companyId] });
    qc.invalidateQueries({ queryKey: ["stock", companyId] });
  };
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    refresh();
  };
  const status = useMutation({
    mutationFn: (to: PurchaseOrderStatusRef) =>
      api(`/purchase-orders/${id}/status`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ toStatusId: to.id, expectedFromStatusId: order.data!.status.id }),
      }),
    onSuccess: (_d, to) => {
      toast("success", `Situação alterada para ${to.label}`);
      refresh();
    },
    onError,
  });
  const cancelRemaining = useMutation({
    mutationFn: () =>
      api<PurchaseOrderView>(`/purchase-orders/${id}/cancel-remaining`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
      }),
    onSuccess: () => {
      toast("success", "Saldo cancelado");
      refresh();
    },
    onError,
  });
  const reverse = useMutation({
    mutationFn: (v: { receipt: PurchaseReceiptView; reason: string }) =>
      api<PurchaseOrderView>(`/purchase-orders/${id}/receipts/${v.receipt.id}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ reason: v.reason.trim() || null }),
      }),
    onSuccess: (_d, v) => {
      toast("success", `Recebimento ${v.receipt.number} estornado`);
      refresh();
    },
    onError,
  });
  const [receiving, setReceiving] = useState(false);
  const [canceling, setCanceling] = useState<PurchaseOrderStatusRef | null>(null);
  const [cancelBalance, setCancelBalance] = useState(false);
  const [reversing, setReversing] = useState<PurchaseReceiptView | null>(null);
  const [reason, setReason] = useState("");

  const o = order.data;
  const canUpdate = can(perms, "purchase_orders", "update");
  const busy = status.isPending || cancelRemaining.isPending || reverse.isPending;
  const cancelTo = o?.nextStatuses.find((s) => s.baseState === "canceled") ?? null;
  const hasActive = !!o?.receipts.some((r) => !r.reversedAt);
  return (
    <>
      <ContentHeader
        title={o ? `Pedido de compra nº ${o.number}` : "Pedido de compra"}
        breadcrumb={["Suprimentos", "Pedidos de compra", o ? String(o.number) : ""]}
        actions={
          o && (
            <>
              <Link to="/suprimentos/pedidos-de-compra" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {canUpdate && o.canReceive && (
                <button type="button" className="btn btn-primary" onClick={() => setReceiving(true)}>
                  Receber
                </button>
              )}
              {canUpdate && o.canReceive && hasActive && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={busy}
                  onClick={() => setCancelBalance(true)}
                >
                  Cancelar saldo
                </button>
              )}
              {canUpdate && !o.lockedReason && (
                <Link
                  to="/suprimentos/pedidos-de-compra/$id/editar"
                  params={{ id: o.id }}
                  className="btn btn-outline-primary"
                >
                  Editar
                </Link>
              )}
              {canUpdate && cancelTo && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={busy}
                  onClick={() => setCanceling(cancelTo)}
                >
                  Cancelar pedido
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={order.isLoading} error={order.error} />
      {o && (
        <div className="row">
          <div className="col-xl-9">
            <div className="box card p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span>Situação:</span>
                <StatusBadge tone={o.status.color}>{o.status.label}</StatusBadge>
                <span className="text-muted small" data-testid="po-progress">
                  {PROGRESS_LABELS[o.progress]}
                </span>
                {canUpdate && o.nextStatuses.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 ms-md-3" role="group" aria-label="Alterar situação">
                    {o.nextStatuses
                      .filter((s) => s.baseState !== "canceled")
                      .map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={busy}
                          onClick={() => status.mutate(s)}
                        >
                          Marcar como {s.label}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <dl className="row mb-0">
                <Info label="Fornecedor">
                  {o.supplier.name}
                  {o.supplier.document && (
                    <span className="text-muted ms-2">{formatDocument(o.supplier.document)}</span>
                  )}
                </Info>
                <Info label="Data">{formatDateBR(o.date)}</Info>
                <Info label="Data prevista">{o.expectedDate && formatDateBR(o.expectedDate)}</Info>
                <Info label="Ordem de compra">{o.purchaseRef}</Info>
                <Info label="Depósito de entrada">{o.warehouse?.name ?? "Padrão da empresa"}</Info>
              </dl>
            </div>
            <div className="box card mb-3">
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Itens do pedido de compra">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Descrição</th>
                      <th scope="col">Un</th>
                      <th scope="col" className="text-end">
                        Qtd
                      </th>
                      <th scope="col" className="text-end">
                        Recebido
                      </th>
                      <th scope="col" className="text-end">
                        Saldo
                      </th>
                      <th scope="col" className="text-end">
                        Preço un.
                      </th>
                      <th scope="col" className="text-end">
                        IPI
                      </th>
                      <th scope="col" className="text-end">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map((it) => (
                      <tr key={it.id}>
                        <td>{it.position}</td>
                        <td>
                          {it.description}
                          {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                        </td>
                        <td>{it.unit ?? "—"}</td>
                        <td className="text-end">{formatQty(it.qtyQ4)}</td>
                        <td className="text-end">{formatQty(it.receivedQ4)}</td>
                        <td className="text-end">
                          {formatQty(it.remainingQ4)}
                          {it.canceledQ4 > 0 && (
                            <div className="text-muted small">{formatQty(it.canceledQ4)} cancelado</div>
                          )}
                        </td>
                        <td className="text-end">{formatBRL(it.unitPriceCents)}</td>
                        <td className="text-end">{it.ipiPct4 ? formatPct(it.ipiPct4) : "—"}</td>
                        <td className="text-end">{formatBRL(it.totalCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Recebimentos</h2>
              {o.receipts.length === 0 ? (
                <p className="text-muted small mb-0">
                  Nada recebido ainda. Use “Receber” a cada entrega ou lance a NF-e de entrada do fornecedor
                  vinculada a este pedido.
                </p>
              ) : (
                <ol className="list-unstyled mb-0" aria-label="Linha do tempo dos recebimentos">
                  {[...o.receipts].reverse().map((r) => (
                    <li key={r.id} className="border-start border-3 ps-3 pb-3" data-testid="po-receipt">
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <strong>{receiptTitle(r)}</strong>
                        <span className="text-muted small">{formatDateBR(r.date)}</span>
                        {r.reversedAt ? (
                          <StatusBadge tone="secondary">Estornado</StatusBadge>
                        ) : (
                          <StatusBadge tone="success">{RECEIPT_SOURCE_LABELS[r.source]}</StatusBadge>
                        )}
                        <span className="ms-auto fw-semibold">{formatBRL(r.valueCents)}</span>
                      </div>
                      <div className="small text-muted">
                        {receiptEffects(r)}
                        {r.warehouse && ` · ${r.warehouse.name}`}
                      </div>
                      <ul className="small mb-1">
                        {r.items.map((l) => (
                          <li key={l.itemId}>
                            {formatQty(l.qtyQ4)} × {l.description}
                            {l.lots.length > 0 && (
                              <span className="text-muted">
                                {" "}
                                · lotes {l.lots.map((x) => `${x.code} (${formatQty(x.qtyQ4)})`).join(", ")}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                      {r.notes && <div className="small">{r.notes}</div>}
                      {r.reversedAt && (
                        <div className="small text-muted">
                          Estornado em {dateTimeBR(r.reversedAt)}
                          {r.reverseReason && ` — ${r.reverseReason}`}
                        </div>
                      )}
                      {r.source === "inbound_dfe" &&
                        r.inboundDfe &&
                        can(perms, "inbound_invoices", "read") && (
                          <Link
                            to="/suprimentos/notas-de-entrada/$id"
                            params={{ id: r.inboundDfe.id }}
                            className="small"
                          >
                            Abrir a nota de entrada
                          </Link>
                        )}
                      {canUpdate && !r.reversedAt && r.source !== "inbound_dfe" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={busy}
                          onClick={() => {
                            setReason("");
                            setReversing(r);
                          }}
                        >
                          Estornar recebimento {r.number}
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Pagamento e transporte</h2>
              <dl className="row mb-0">
                <Info label="Condição de pagamento">{o.paymentTerm?.name ?? "À vista"}</Info>
                <Info label="Categoria das contas">
                  {o.financialCategory?.name ?? "Compras de mercadorias"}
                </Info>
                <Info label="Transportador">{o.carrier?.name}</Info>
                <Info label="Frete">{FREIGHT_MODE_LABELS[o.freightMode]}</Info>
              </dl>
              {o.installments.length > 0 && (
                <table className="table table-sm w-auto mt-2" aria-label="Parcelas previstas">
                  <thead>
                    <tr>
                      <th scope="col">Parcela</th>
                      <th scope="col">Vencimento previsto</th>
                      <th scope="col" className="text-end">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.installments.map((p) => (
                      <tr key={p.number}>
                        <td>{p.number}</td>
                        <td>{formatDateBR(p.dueDate)}</td>
                        <td className="text-end">{formatBRL(p.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {o.notes && (
                <>
                  <h3 className="h6 mt-3">Observações</h3>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {o.notes}
                  </p>
                </>
              )}
              {o.internalNotes && (
                <>
                  <h3 className="h6 mt-3">Observações internas</h3>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {o.internalNotes}
                  </p>
                </>
              )}
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Histórico de situações</h2>
              <QueryStatus loading={history.isLoading} error={history.error} />
              {history.data && history.data.data.length === 0 && (
                <p className="text-muted small mb-0">Nenhuma mudança de situação ainda.</p>
              )}
              {history.data && history.data.data.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm mb-0" aria-label="Histórico de situações">
                    <thead>
                      <tr>
                        <th scope="col">Quando</th>
                        <th scope="col">Mudança</th>
                        <th scope="col">Origem</th>
                        <th scope="col">Usuário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.data.map((h) => (
                        <tr key={h.id}>
                          <td className="text-nowrap">{dateTimeBR(h.createdAt)}</td>
                          <td>{purchaseHistoryLabel(h)}</td>
                          <td>{h.automatic ? "Automática (recebimento)" : "Manual"}</td>
                          <td>{h.userName ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          <aside className="col-xl-3">
            <div className="box card p-3 mb-3">
              <h2 className="h6">Totais</h2>
              <dl className="row mb-0 small">
                {(
                  [
                    ["Total dos produtos", o.productsTotalCents],
                    ["IPI", o.ipiTotalCents],
                    ["ICMS-ST", o.icmsStCents],
                    [
                      o.discountPct4 !== null ? `Desconto (${formatPct(o.discountPct4)})` : "Desconto",
                      -o.discountCents,
                    ],
                    ["Frete", o.freightCents],
                    ["Outras despesas", o.otherExpensesCents],
                  ] as const
                ).map(([label, v]) => (
                  <Fragment key={label}>
                    <dt className="col-7 fw-normal">{label}</dt>
                    <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
                  </Fragment>
                ))}
                <dt className="col-7">Total do pedido</dt>
                <dd className="col-5 text-end fw-bold mb-1" data-testid="po-total">
                  {formatBRL(o.totalCents)}
                </dd>
                <dt className="col-7 fw-normal">Recebido</dt>
                <dd className="col-5 text-end mb-0" data-testid="po-received">
                  {formatBRL(o.receivedCents)}
                </dd>
              </dl>
              {o.lockedReason && <p className="text-muted small mt-2 mb-0">{o.lockedReason}</p>}
            </div>
          </aside>
        </div>
      )}
      {o && receiving && (
        <ReceiveDialog
          order={o}
          onClose={() => setReceiving(false)}
          onDone={() => {
            setReceiving(false);
            refresh();
          }}
        />
      )}
      <ConfirmDialog
        open={!!canceling}
        title="Cancelar pedido de compra"
        message={`Cancelar o pedido nº ${o?.number ?? ""}? Os recebimentos lançados são estornados (estoque e contas a pagar).`}
        confirmLabel="Cancelar pedido"
        onConfirm={() => {
          if (canceling) status.mutate(canceling);
          setCanceling(null);
        }}
        onCancel={() => setCanceling(null)}
      />
      <ConfirmDialog
        open={cancelBalance}
        title="Cancelar saldo"
        message="O que falta receber deixa de ser esperado e o pedido fica como atendido. Para receber depois, volte a situação do pedido."
        confirmLabel="Cancelar saldo"
        onConfirm={() => {
          cancelRemaining.mutate();
          setCancelBalance(false);
        }}
        onCancel={() => setCancelBalance(false)}
      />
      <Modal
        open={!!reversing}
        title={reversing ? `Estornar recebimento ${reversing.number}` : "Estornar recebimento"}
        onClose={() => setReversing(null)}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setReversing(null)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={reverse.isPending}
              onClick={() => {
                if (reversing) reverse.mutate({ receipt: reversing, reason });
                setReversing(null);
              }}
            >
              Estornar
            </button>
          </>
        }
      >
        <p>
          A entrada no estoque é estornada e as contas a pagar deste recebimento são canceladas (conta com
          baixa impede o estorno). O saldo volta ao pedido.
        </p>
        <Field label="Motivo">
          <input value={reason} maxLength={500} data-autofocus onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}

/** "Receber": quantidade por item (padrão = saldo), data, depósito e o que lançar (estoque e/ou contas). */
function ReceiveDialog({
  order,
  onClose,
  onDone,
}: {
  order: PurchaseOrderView;
  onClose(): void;
  onDone(): void;
}) {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const toast = useToast();
  const canStock = can(perms, "stock", "create");
  const canPayables = can(perms, "payables", "create");
  const options = useQuery({
    queryKey: ["purchase-orders", companyId, "form-options"],
    queryFn: () => api<PurchaseOrderFormOptions>("/purchase-orders/form-options"),
  });
  const [rows, setRows] = useState<ReceiveRow[]>(() => receiveDefaults(order));
  const [date, setDate] = useState(todayInSaoPaulo());
  const [warehouseId, setWarehouseId] = useState(order.warehouse?.id ?? "");
  const [postStock, setPostStock] = useState(canStock);
  const [postPayables, setPostPayables] = useState(canPayables);
  const [notes, setNotes] = useState("");
  // Uma chave por diálogo: clique duplo ou repetição da rede não lançam duas vezes.
  const [idemKey] = useState(() => crypto.randomUUID());
  const problem =
    receiveProblem(order, rows) ??
    (!postStock && !postPayables ? "Escolha lançar o estoque, as contas a pagar ou os dois." : null);
  const save = useMutation({
    mutationFn: () =>
      api<PurchaseOrderView>(`/purchase-orders/${order.id}/receipts`, {
        method: "POST",
        headers: { "Idempotency-Key": idemKey },
        body: JSON.stringify({
          date,
          warehouseId: warehouseId || null,
          items: receiveItems(rows),
          postStock,
          postPayables,
          notes: notes.trim() || null,
        }),
      }),
    onSuccess: (v) => {
      toast("success", `Recebimento ${v.receipts.at(-1)?.number ?? ""} lançado`);
      onDone();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const byId = new Map(order.items.map((i) => [i.id, i]));
  return (
    <Modal
      open
      title={`Receber pedido de compra nº ${order.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!!problem || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Lançando…" : "Lançar recebimento"}
          </button>
        </>
      }
    >
      <div className="table-responsive">
        <table className="table table-sm align-middle" aria-label="Itens a receber">
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col" className="text-end">
                Saldo
              </th>
              <th scope="col" style={{ width: "8rem" }} className="text-end">
                Receber
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const it = byId.get(r.itemId)!;
              const setLots = (lots: ReceiveLotRow[]) =>
                setRows((xs) => xs.map((x) => (x.itemId === r.itemId ? { ...x, lots } : x)));
              const lots = r.lots ?? [];
              return (
                <Fragment key={r.itemId}>
                  <tr>
                    <td>{it.description}</td>
                    <td className="text-end">
                      {formatQty(it.remainingQ4)} {it.unit ?? ""}
                    </td>
                    <td>
                      <QtyInput
                        aria-label={`Quantidade recebida de ${it.description}`}
                        className="form-control form-control-sm text-end"
                        value={r.qtyQ4}
                        onChange={(v) =>
                          setRows((xs) => xs.map((x) => (x.itemId === r.itemId ? { ...x, qtyQ4: v } : x)))
                        }
                      />
                    </td>
                  </tr>
                  {it.batchControl && (
                    <tr>
                      <td colSpan={3} className="border-top-0 pt-0">
                        <ReceiveLots description={it.description} lots={lots} onChange={setLots} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="row">
        <Field label="Data do recebimento" className="col-sm-6">
          <input type="date" value={date} max={todayInSaoPaulo()} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Depósito" className="col-sm-6">
          <select
            className="form-select"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            <option value="">Padrão da empresa</option>
            {options.data?.warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="form-check">
        <input
          id="po-post-stock"
          type="checkbox"
          className="form-check-input"
          checked={postStock}
          disabled={!canStock}
          onChange={(e) => setPostStock(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="po-post-stock">
          Lançar a entrada no estoque (com o custo do pedido)
        </label>
      </div>
      <div className="form-check mb-3">
        <input
          id="po-post-payables"
          type="checkbox"
          className="form-check-input"
          checked={postPayables}
          disabled={!canPayables}
          onChange={(e) => setPostPayables(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="po-post-payables">
          Lançar as contas a pagar ({order.paymentTerm?.name ?? "à vista"}, a partir da data do recebimento)
        </label>
      </div>
      <Field label="Observações">
        <input value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      {problem && (
        <p className="text-danger small mb-0" role="alert">
          {problem}
        </p>
      )}
    </Modal>
  );
}

/** Lotes de um item no "Receber" (produto com controle de lotes). O que não for a lote entra "Sem lote". */
function ReceiveLots({
  description,
  lots,
  onChange,
}: {
  description: string;
  lots: ReceiveLotRow[];
  onChange(v: ReceiveLotRow[]): void;
}) {
  const set = (i: number, patch: Partial<ReceiveLotRow>) =>
    onChange(lots.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="ps-3 small">
      {lots.map((l, i) => (
        <div key={i} className="row g-1 align-items-center mb-1">
          <div className="col-6 col-md-3">
            <input
              className="form-control form-control-sm"
              aria-label={`Lote ${i + 1} de ${description}`}
              placeholder="Lote"
              maxLength={20}
              value={l.code}
              onChange={(e) => set(i, { code: e.target.value })}
            />
          </div>
          <div className="col-6 col-md-3">
            <input
              type="date"
              className="form-control form-control-sm"
              aria-label={`Fabricação do lote ${i + 1} de ${description}`}
              title="Fabricação"
              value={l.manufacturedAt}
              onChange={(e) => set(i, { manufacturedAt: e.target.value })}
            />
          </div>
          <div className="col-6 col-md-3">
            <input
              type="date"
              className="form-control form-control-sm"
              aria-label={`Validade do lote ${i + 1} de ${description}`}
              title="Validade"
              value={l.expiresAt}
              onChange={(e) => set(i, { expiresAt: e.target.value })}
            />
          </div>
          <div className="col-4 col-md-2">
            <QtyInput
              aria-label={`Quantidade do lote ${i + 1} de ${description}`}
              className="form-control form-control-sm text-end"
              value={l.qtyQ4}
              onChange={(q) => set(i, { qtyQ4: q })}
            />
          </div>
          <div className="col-2 col-md-1">
            <button
              type="button"
              className="btn btn-sm btn-link text-danger p-0"
              aria-label={`Remover lote ${i + 1} de ${description}`}
              onClick={() => onChange(lots.filter((_, j) => j !== i))}
            >
              Remover
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-sm btn-link p-0"
        onClick={() => onChange([...lots, { code: "", manufacturedAt: "", expiresAt: "", qtyQ4: null }])}
      >
        Informar lote de {description}
      </button>
    </div>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 col-lg-3 fw-normal text-muted">{label}</dt>
      <dd className="col-sm-8 col-lg-9">{children || "—"}</dd>
    </>
  );
}
